import { motion } from "framer-motion";

export default function Illustration() {
  return (
    <div className="relative overflow-hidden rounded-2xl aspect-[4/3]">
      <svg
        viewBox="0 0 640 400"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full block rounded-2xl"
        style={{ boxShadow: "0 12px 40px rgba(12,192,223,.15)" }}
        data-hero-illustration="true"
      >
        <defs>
          <linearGradient id="bgGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#f0fbfd" />
            <stop offset="100%" stopColor="#e2f6fa" />
          </linearGradient>
          <linearGradient id="accentGrad" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#0cc0df" />
            <stop offset="100%" stopColor="#0a9db5" />
          </linearGradient>
          <linearGradient id="dotGrad" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stopColor="#c5f1f8" />
            <stop offset="100%" stopColor="#0cc0df" />
          </linearGradient>
          <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="cardShadow" x="-5%" y="-5%" width="110%" height="115%">
            <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor="#0cc0df" floodOpacity="0.1" />
          </filter>
          <clipPath id="photoClip">
            <rect x="60" y="68" width="204" height="264" rx="10" />
          </clipPath>
        </defs>

        {/* Background */}
        <rect width="640" height="400" rx="18" fill="url(#bgGrad)" />

        {/* Main card */}
        <rect x="24" y="24" width="592" height="352" rx="14" fill="#ffffff" filter="url(#cardShadow)" />

        {/* Brand accent top bar */}
        <rect x="24" y="24" width="592" height="4" rx="2" fill="url(#accentGrad)" opacity="0.7" />

        {/* Pulsing accent dot */}
        <motion.circle
          cx="570"
          cy="54"
          r="5"
          fill="url(#dotGrad)"
          filter="url(#softGlow)"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.7, 1, 0.7]
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{ transformOrigin: "570px 54px" }}
        />

        {/* Photo frame with brand border */}
        <rect x="48" y="56" width="228" height="288" rx="12" fill="#ffffff" stroke="#0cc0df" strokeWidth="1.5" strokeOpacity="0.25" />
        <rect x="60" y="68" width="204" height="264" rx="10" fill="#daf4f8" />

        {/* Floating nurse image */}
        <motion.g
          animate={{
            y: [0, -3, 0]
          }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        >
          <image
            href="/images/nurse-illustration.png"
            x="60"
            y="68"
            width="204"
            height="264"
            preserveAspectRatio="xMidYMid meet"
            clipPath="url(#photoClip)"
          />
        </motion.g>

        {/* Info area */}
        <g transform="translate(304,64)">
          {/* Line 1: Name */}
          <motion.rect
            x="0" y="0" height="12" rx="6" fill="#334155" opacity="0.6"
            initial={{ width: 0 }}
            animate={{ width: 220 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.3 }}
          />

          {/* Line 2: Subtitle */}
          <motion.rect
            x="0" y="20" height="10" rx="5" fill="#94a3b8" opacity="0.5"
            initial={{ width: 0 }}
            animate={{ width: 160 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.6 }}
          />

          {/* Line 3: Role badge */}
          <motion.rect
            x="0" y="46" height="24" rx="12" fill="#0cc0df" opacity="0.1"
            initial={{ width: 0 }}
            animate={{ width: 140 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.9 }}
          />
          <motion.rect
            x="10" y="53" height="10" rx="5" fill="#0cc0df" opacity="0.45"
            initial={{ width: 0 }}
            animate={{ width: 80 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.9 }}
          />

          {/* Divider 1 */}
          <motion.line
            x1="0" y1="86" y2="86" stroke="#e2e8f0" strokeWidth="1"
            initial={{ x2: 0 }}
            animate={{ x2: 280 }}
            transition={{ duration: 0.4, ease: "easeOut", delay: 1.2 }}
          />

          {/* Detail row 1 */}
          <motion.g
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 1.4 }}
          >
            <circle cx="6" cy="106.5" r="5.5" fill="#22c55e" opacity="0.1" />
            <path d="M3 106.5 L5 108.5 L9 103.5" fill="none" stroke="#22c55e" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </motion.g>
          <motion.rect
            x="18" y="102" height="9" rx="4.5" fill="#e2e8f0"
            initial={{ width: 0 }}
            animate={{ width: 232 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 1.4 }}
          />

          {/* Detail row 2 */}
          <motion.g
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 1.7 }}
          >
            <circle cx="6" cy="124.5" r="5.5" fill="#22c55e" opacity="0.1" />
            <path d="M3 124.5 L5 126.5 L9 121.5" fill="none" stroke="#22c55e" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </motion.g>
          <motion.rect
            x="18" y="120" height="9" rx="4.5" fill="#f1f5f9"
            initial={{ width: 0 }}
            animate={{ width: 192 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 1.7 }}
          />

          {/* Detail row 3 */}
          <motion.g
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 2.0 }}
          >
            <circle cx="6" cy="142.5" r="5.5" fill="#22c55e" opacity="0.1" />
            <path d="M3 142.5 L5 144.5 L9 139.5" fill="none" stroke="#22c55e" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </motion.g>
          <motion.rect
            x="18" y="138" height="9" rx="4.5" fill="#f1f5f9"
            initial={{ width: 0 }}
            animate={{ width: 212 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 2.0 }}
          />

          {/* Divider 2 */}
          <motion.line
            x1="0" y1="160" y2="160" stroke="#e2e8f0" strokeWidth="1"
            initial={{ x2: 0 }}
            animate={{ x2: 280 }}
            transition={{ duration: 0.4, ease: "easeOut", delay: 2.3 }}
          />

          {/* Transport icons */}
          {/* Car - green */}
          <motion.g
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: [1, 1.08, 1] }}
            transition={{
              opacity: { duration: 0.3, delay: 2.5 },
              scale: { duration: 2, repeat: Infinity, ease: "easeInOut", delay: 2.5 }
            }}
            style={{ transformOrigin: "18px 190px" }}
          >
            <rect x="0" y="172" width="36" height="36" rx="10" fill="#dcfce7" />
            <path d="M8 186 L12 180 L24 180 L28 186 Z" fill="#22c55e" />
            <rect x="6" y="186" width="24" height="8" rx="2" fill="#22c55e" />
            <rect x="13" y="181" width="4" height="3.5" rx="0.8" fill="#dcfce7" />
            <rect x="19" y="181" width="5" height="3.5" rx="0.8" fill="#dcfce7" />
            <circle cx="12" cy="195.5" r="2.5" fill="#15803d" />
            <circle cx="24" cy="195.5" r="2.5" fill="#15803d" />
          </motion.g>

          {/* Bicycle - red */}
          <motion.g
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: [1, 1.08, 1] }}
            transition={{
              opacity: { duration: 0.3, delay: 2.8 },
              scale: { duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: 2.8 }
            }}
            style={{ transformOrigin: "62px 190px" }}
          >
            <rect x="44" y="172" width="36" height="36" rx="10" fill="#fef2f2" />
            <circle cx="54" cy="195" r="5" fill="none" stroke="#ef4444" strokeWidth="1.3" />
            <circle cx="70" cy="195" r="5" fill="none" stroke="#ef4444" strokeWidth="1.3" />
            <polyline points="54,195 62,183 70,195" fill="none" stroke="#ef4444" strokeWidth="1.5" strokeLinejoin="round" />
            <line x1="58" y1="183" x2="66" y2="183" stroke="#ef4444" strokeWidth="1.3" strokeLinecap="round" />
            <line x1="62" y1="183" x2="62" y2="180" stroke="#ef4444" strokeWidth="1.3" strokeLinecap="round" />
            <line x1="60" y1="180" x2="64" y2="180" stroke="#ef4444" strokeWidth="1.3" strokeLinecap="round" />
          </motion.g>

          {/* Competency tags - purple (write-in then pulse) */}
          <motion.g
            animate={{ scale: [1, 1.06, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 3.8 }}
            style={{ transformOrigin: "39px 231px" }}
          >
            <motion.rect
              x="0" y="220" height="22" rx="11" fill="#8b5cf6" opacity="0.1"
              initial={{ width: 0 }}
              animate={{ width: 78 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 3.1 }}
            />
            <motion.rect
              x="10" y="227" height="8" rx="4" fill="#8b5cf6" opacity="0.3"
              initial={{ width: 0 }}
              animate={{ width: 50 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 3.1 }}
            />
          </motion.g>

          <motion.g
            animate={{ scale: [1, 1.06, 1] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: 4.0 }}
            style={{ transformOrigin: "117px 231px" }}
          >
            <motion.rect
              x="84" y="220" height="22" rx="11" fill="#8b5cf6" opacity="0.1"
              initial={{ width: 0 }}
              animate={{ width: 66 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 3.1 }}
            />
            <motion.rect
              x="94" y="227" height="8" rx="4" fill="#8b5cf6" opacity="0.3"
              initial={{ width: 0 }}
              animate={{ width: 40 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 3.1 }}
            />
          </motion.g>

          <motion.g
            animate={{ scale: [1, 1.06, 1] }}
            transition={{ duration: 1.9, repeat: Infinity, ease: "easeInOut", delay: 4.2 }}
            style={{ transformOrigin: "192px 231px" }}
          >
            <motion.rect
              x="156" y="220" height="22" rx="11" fill="#8b5cf6" opacity="0.1"
              initial={{ width: 0 }}
              animate={{ width: 72 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 3.1 }}
            />
            <motion.rect
              x="166" y="227" height="8" rx="4" fill="#8b5cf6" opacity="0.3"
              initial={{ width: 0 }}
              animate={{ width: 46 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 3.1 }}
            />
          </motion.g>
        </g>
      </svg>
    </div>
  );
}
